/**
 * AppShellSkeleton - native app-like skeleton for the Suspense fallback.
 *
 * Renders the REAL DashboardLayout chrome (sidebar + mobile header + bottom
 * tab bar) from cached user+business in localStorage, with grey shimmer
 * blocks in the same shape as the dashboard cards. This eliminates the
 * "white flash" users see after login while the DashboardPage code chunk
 * is still downloading or its data is still fetching.
 *
 * Falls back gracefully to a neutral shell if nothing is cached.
 */
import React from 'react';

const LOGO_FULL = '/akountfly-logo-full.png';
const LOGO_ICON = '/akountfly-icon.png';

const readCachedBusiness = () => {
  try {
    const raw = localStorage.getItem('akountfly_business');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const Shimmer = ({ className = '' }) => (
  <div
    className={
      'relative overflow-hidden bg-slate-100 ' +
      'before:absolute before:inset-0 before:-translate-x-full ' +
      'before:animate-[shimmer_1.4s_infinite] ' +
      'before:bg-gradient-to-r before:from-transparent before:via-white/70 before:to-transparent ' +
      className
    }
  />
);

// Best-effort static sidebar nav (matches DashboardLayout.businessNavItems)
const NAV_LABELS = [
  'Home', 'Invoices', 'Bills', 'Insights', 'AI CFO',
  'Transactions', 'Bank Accounts', 'Reports', 'Budgets',
  'Products & Contacts', 'More Tools',
];

const MOBILE_TABS = ['Home', 'Invoices', 'Bills', 'Insights', 'AI CFO'];

// Map the current URL to the nav label it belongs to so the skeleton
// highlights the page being loaded (not always "Home").
const NAV_PATHS = [
  ['/dashboard', 'Home'], ['/invoices', 'Invoices'], ['/bills', 'Bills'], ['/insights', 'Insights'],
  ['/akountfly-ai', 'AI CFO'], ['/chat', 'AI CFO'], ['/bank-feeds', 'Transactions'], ['/transactions', 'Transactions'],
  ['/banks', 'Bank Accounts'], ['/reports', 'Reports'], ['/budget', 'Budgets'], ['/products-services', 'Products & Contacts'],
];
const activeNavLabel = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (path === '/' || path === '/auth' || path === '/login') return 'Home';
  const hit = NAV_PATHS.find(([p]) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`));
  return hit ? hit[1] : 'More Tools';
};

export default function AppShellSkeleton() {
  const business = readCachedBusiness();
  const active = activeNavLabel();
  const isHome = active === 'Home';
  const businessName = business?.business_name || 'AkountFly';
  const initial = businessName.charAt(0).toUpperCase();

  return (
    <div
      className="min-h-dvh bg-[#F8FAFC]"
      data-testid="app-shell-skeleton"
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 lg:flex-col bg-white border-r border-slate-200">
        <div className="flex flex-col h-screen">
          <div className="px-3 py-2 border-b border-slate-200">
            <img src={LOGO_FULL} alt="AkountFly" className="max-w-[112px] h-auto object-contain" />
          </div>
          <div className="px-3 pt-3 pb-2 flex items-center gap-2">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0"
              style={{ background: 'linear-gradient(135deg, #283F8B, #108981)' }}
            >
              {initial}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{businessName}</p>
              <p className="text-[11px] text-slate-500 truncate">{business?.industry || 'Loading'}</p>
            </div>
          </div>
          <nav className="flex-1 px-2 py-2 space-y-1 overflow-hidden">
            {NAV_LABELS.map((label, i) => (
              <div key={label} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${label === active ? 'bg-[#283F8B]/10' : ''}`}>
                <Shimmer className="w-5 h-5 rounded" />
                <span className={`text-sm font-medium ${label === active ? 'text-[#283F8B]' : 'text-slate-400'}`}>{label}</span>
              </div>
            ))}
          </nav>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between px-4 py-4 min-h-[80px]">
          <img src={LOGO_FULL} alt="AkountFly" style={{ height: '27px', width: 'auto' }} />
          <div className="flex items-center gap-3">
            <Shimmer className="w-10 h-10 rounded-lg" />
            <Shimmer className="w-11 h-11 rounded-lg" />
          </div>
        </div>
      </header>

      {/* Mobile Bottom Tab Bar — safe-area aware (iter1102) */}
      <nav data-mobile-bottom-nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around h-16">
          {MOBILE_TABS.map((label, i) => (
            <div key={label} className="flex flex-col items-center gap-1 p-2">
              <Shimmer className={`w-6 h-6 rounded ${label === active ? '!bg-[#283F8B]/20' : ''}`} />
              <span className={`text-xs font-medium ${label === active ? 'text-[#283F8B]' : 'text-slate-400'}`}>{label.split(' ')[0]}</span>
            </div>
          ))}
        </div>
      </nav>

      {/* Main content area - dashboard cards skeleton */}
      <main className="lg:ml-64 pt-[88px] lg:pt-0 pb-[calc(4rem+env(safe-area-inset-bottom)+1rem)] lg:pb-0 min-h-dvh">
        <div className="p-4 lg:p-6 space-y-4">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-7 w-56 rounded-lg bg-slate-200" />
              <div className="h-4 w-32 rounded bg-slate-100" />
            </div>
            <Shimmer className="w-12 h-12 rounded-full" />
          </div>

          {!isHome && (
            <>
              <div className="rounded-2xl bg-white border border-slate-200 p-4 h-[64px] flex items-center gap-3">
                <Shimmer className="w-9 h-9 rounded-lg" />
                <div className="space-y-2 flex-1"><div className="h-4 w-48 rounded bg-slate-200" /><div className="h-3 w-72 max-w-full rounded bg-slate-100" /></div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="rounded-2xl bg-white border border-slate-200 p-4 h-[72px] flex flex-col justify-between">
                    <div className="h-3 w-20 rounded bg-slate-100" /><div className="h-5 w-16 rounded bg-slate-200" />
                  </div>
                ))}
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="p-4 flex items-center gap-3">
                    <Shimmer className="w-9 h-9 rounded-full" />
                    <div className="space-y-2 flex-1"><div className="h-4 w-40 rounded bg-slate-200" /><div className="h-3 w-24 rounded bg-slate-100" /></div>
                    <div className="h-4 w-20 rounded bg-slate-200" />
                  </div>
                ))}
              </div>
            </>
          )}
          {isHome && (<>
          {/* Metric cards row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { color: 'from-[#108981] to-[#0a6b65]', label: 'Money In' },
              { color: 'from-[#283F8B] to-[#1e3170]', label: 'Money Out' },
              { color: 'from-[#F59E0B] to-[#D97706]', label: 'They Owe You' },
              { color: 'from-[#5FC6C5] to-[#3fa8a6]', label: 'Cash & Bank' },
            ].map((m) => (
              <div key={m.label} className={`rounded-2xl p-4 h-[112px] bg-gradient-to-br ${m.color} flex flex-col justify-between`}>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-white/20" />
                  <span className="text-xs font-medium text-white/80">{m.label}</span>
                </div>
                <div className="space-y-1.5">
                  <div className="h-6 w-24 rounded bg-white/30" />
                  <div className="h-3 w-16 rounded bg-white/20" />
                </div>
              </div>
            ))}
          </div>

          {/* Secondary card */}
          <div className="rounded-2xl bg-white border border-slate-200 p-4 h-[80px] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shimmer className="w-10 h-10 rounded-full" />
              <div className="space-y-2">
                <div className="h-4 w-32 rounded bg-slate-200" />
                <div className="h-4 w-24 rounded bg-slate-100" />
              </div>
            </div>
            <div className="h-4 w-16 rounded bg-slate-100" />
          </div>

          {/* Two action cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {[0, 1].map((i) => (
              <div key={i} className="rounded-2xl bg-white border border-slate-200 p-4 h-[80px] flex items-center gap-3">
                <Shimmer className="w-10 h-10 rounded-lg" />
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-40 rounded bg-slate-200" />
                  <div className="h-3 w-56 rounded bg-slate-100" />
                </div>
              </div>
            ))}
          </div>

          {/* Quick-action grid */}
          <div className="grid grid-cols-4 lg:grid-cols-8 gap-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-white border border-slate-200 p-3 flex flex-col items-center gap-2">
                <Shimmer className="w-8 h-8 rounded-full" />
                <div className="h-3 w-12 rounded bg-slate-100" />
              </div>
            ))}
          </div>
          </>)}
        </div>
      </main>

      {/* Shimmer keyframes injected once */}
      <style>{`
        @keyframes shimmer { 100% { transform: translateX(100%); } }
      `}</style>
    </div>
  );
}
